import { Game } from './core/game';
import { LobbyScreen } from './ui/lobbyScreen';
import { installDesignSystem } from './ui/styles/designSystem';
import type { GameSession } from './network/gameSession';
import type { NetworkManager } from './network/networkManager';
import type { TeamConfig } from './logic/team';
import type { GameSettings } from './core/gameSettings';

let currentGame: Game | null = null;
let lobby: LobbyScreen | null = null;
/** A new service worker is installed; reload once it's safe (in the lobby). */
let pendingSwReload = false;

function getRoomCodeFromUrl(): string | null {
  const hash = window.location.hash;
  // Numeric codes (current) or legacy WORMS-XXXXXX codes.
  const match = hash.match(/^#room\/(\d{4,8}|WORMS-[A-Z0-9]+)$/i);
  return match ? match[1].toUpperCase() : null;
}

function clearRoomFromUrl(): void {
  history.replaceState(null, '', window.location.pathname);
}

function quitToLobby(): void {
  const appContainer = document.getElementById('app');
  if (!appContainer) return;

  if (currentGame) {
    currentGame = null;
  }

  // Apply a pending app update now that no game is in progress.
  if (pendingSwReload) {
    window.location.reload();
    return;
  }

  appContainer.innerHTML = '';

  if (lobby) {
    lobby.showLobby();
  } else {
    lobby = new LobbyScreen(appContainer, {
      onStartGame: (session, teams, networkManager, isHost, terrainSeed, settings) => {
        clearRoomFromUrl();
        startGame(session, teams, networkManager, isHost, terrainSeed, settings);
      },
    });
    lobby.show();
  }
}

function registerServiceWorker() {
  // PWA caching is production-only. In dev the service worker fights Vite HMR
  // and can serve a stale app shell / miss newly added public assets (e.g. a
  // new tank sprite) - so skip it and proactively unregister any SW left over
  // from a previous prod-build visit on the same origin (localhost).
  if (!import.meta.env.PROD) {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const r of regs) r.unregister();
      });
    }
    return;
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                // Never yank the page out from under an active game: reload
                // immediately only in the lobby, otherwise defer to quit time.
                if (currentGame) {
                  pendingSwReload = true;
                } else {
                  window.location.reload();
                }
              }
            });
          }
        });

        registration.update();
      })
      .catch((err) => {
        console.warn('SW registration failed:', err);
      });
  }
}

/**
 * Detect a stale (cached) PWA on load: ask the server for the current build id
 * and, if it differs from the one baked into this bundle, drop all caches +
 * the service worker and hard-reload onto the new build. Fire-and-forget so it
 * never delays startup; only an out-of-date client pays the reload.
 */
async function checkAppVersion(): Promise<void> {
  if (__GIT_HASH__ === 'dev' || !navigator.onLine) return;
  try {
    const res = await fetch('/version', { cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json().catch(() => null);
    const latest = data?.version as string | undefined;
    if (!latest || latest === __GIT_HASH__) return;

    // Guard against a reload loop if a refresh somehow doesn't update the build.
    const KEY = 'vv:reloadedForVersion';
    if (sessionStorage.getItem(KEY) === latest) return;
    sessionStorage.setItem(KEY, latest);

    console.warn(`[version] running ${__GIT_HASH__}, latest is ${latest} — refreshing`);
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    window.location.reload();
  } catch {
    // Offline / network / parse error: keep running the cached app.
  }
}

async function startGame(
  session: GameSession,
  teams: TeamConfig[],
  networkManager: NetworkManager | null,
  isHost: boolean,
  terrainSeed?: number,
  settings?: GameSettings,
) {
  const appContainer = document.getElementById('app');
  if (!appContainer) return;

  lobby?.hide();

  currentGame = new Game({
    container: appContainer,
    teams,
    session,
    networkManager: networkManager ?? undefined,
    isHost,
    terrainSeed,
    settings,
    onQuitToLobby: () => {
      quitToLobby();
    },
  });

  (window as any).game = currentGame;

  try {
    await currentGame.init();
    currentGame.start();

    console.log('VVorms started!');
    console.log('Controls:');
    console.log('  Arrow keys / WASD: Move and aim');
    console.log('  Enter: Jump (double-tap for backflip)');
    console.log('  Space: Charge and fire weapon');
    console.log('  Tab: Open weapon panel');
    console.log('  1-0: Select weapons');
  } catch (error) {
    console.error('Failed to initialize game:', error);
    appContainer.innerHTML = `
      <div style="color: #e94560; text-align: center; padding: 20px;">
        <h2>Failed to load game</h2>
        <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    `;
  }
}

async function main() {
  // Stale-PWA check: reloads onto the latest build if this one is out of date.
  void checkAppVersion();

  installDesignSystem();

  const appContainer = document.getElementById('app');
  if (!appContainer) {
    throw new Error('App container not found');
  }

  appContainer.innerHTML = '';

  const roomCodeFromUrl = getRoomCodeFromUrl();

  lobby = new LobbyScreen(appContainer, {
    onStartGame: (session, teams, networkManager, isHost, terrainSeed, settings) => {
      clearRoomFromUrl();
      startGame(session, teams, networkManager, isHost, terrainSeed, settings);
    },
  });

  if (roomCodeFromUrl) {
    lobby.showAndJoinRoom(roomCodeFromUrl);
  } else {
    lobby.show();
  }

  registerServiceWorker();
}

main();
